import { ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import type { ReactNode } from "react";

type ButtonProps = {
  children: ReactNode;
  to?: string;
  href?: string;
  external?: boolean;
  download?: boolean | string;
  type?: "button" | "submit";
  variant?: "primary" | "secondary" | "outline" | "ghost" | "textLink" | "text" | "darkAccent" | "dark" | "darkSecondary" | "darkGhost";
  size?: "sm" | "md" | "lg";
  calm?: boolean;
  arrow?: boolean;
  onClick?: () => void;
  className?: string;
  disabled?: boolean;
  loading?: boolean;
};

const sizes = {
  sm: "min-h-11 px-5 py-2 text-sm",
  md: "min-h-[52px] px-7 py-2.5 text-base",
  lg: "min-h-14 px-8 py-3 text-base"
};

const primarySurface = "brand-gradient shadow-[0_8px_20px_rgba(35,100,190,0.22),inset_0_1px_0_rgba(255,255,255,0.22)] hover:shadow-[0_12px_26px_rgba(35,100,190,0.30),inset_0_1px_0_rgba(255,255,255,0.26)] active:shadow-[0_4px_10px_rgba(35,100,190,0.18),inset_0_1px_0_rgba(255,255,255,0.18)]";
const calmSurface = "brand-gradient-calm shadow-[0_6px_16px_rgba(35,100,190,0.18),inset_0_1px_0_rgba(255,255,255,0.20)] hover:shadow-[0_10px_22px_rgba(35,100,190,0.24),inset_0_1px_0_rgba(255,255,255,0.24)] active:shadow-[0_3px_8px_rgba(35,100,190,0.14),inset_0_1px_0_rgba(255,255,255,0.16)]";
const hoverNudge = "hover:-translate-y-[2px] hover:brightness-[1.03] hover:saturate-[1.05] active:scale-[0.985]";
const disabledGray = "disabled:bg-none disabled:bg-[#E7E4DC] disabled:text-ink-secondary disabled:border-line disabled:shadow-none disabled:opacity-100";
const darkField = "focus-visible:ring-offset-brand-dark";

const variants: Record<string, string> = {
  primary: `text-white border-[rgba(35,100,190,0.28)] ${hoverNudge} ${disabledGray} focus-visible:ring-offset-bg-primary`,
  secondary: "border-[#D6D2C9] text-ink-primary bg-[linear-gradient(180deg,rgba(255,255,255,0.72),rgba(244,241,233,0.94))] shadow-[inset_0_1px_0_rgba(255,255,255,0.85),0_3px_8px_rgba(29,42,52,0.045)] hover:border-brand-cyan/50 hover:-translate-y-[1px] hover:shadow-[inset_0_1px_0_rgba(255,255,255,0.9),0_5px_14px_rgba(29,42,52,0.07)] focus-visible:ring-offset-bg-primary",
  outline: "border-line text-ink-primary bg-transparent hover:border-brand-cyan/50 hover:bg-surface focus-visible:ring-offset-bg-primary",
  ghost: "border-transparent text-ink-primary hover:text-brand-blue focus-visible:ring-offset-bg-primary",
  textLink: "min-h-0 rounded-md border-transparent bg-transparent px-0 py-0 text-brand-blue hover:text-brand-blueDark shadow-none focus-visible:ring-offset-bg-primary",
  text: "min-h-0 rounded-md border-transparent bg-transparent px-0 py-0 text-brand-blue hover:text-brand-blueDark shadow-none focus-visible:ring-offset-bg-primary",
  darkAccent: `text-white border-[rgba(9,100,120,0.4)] bg-[linear-gradient(135deg,#0B87AD_0%,#0C6E56_100%)] shadow-[inset_0_1px_0_rgba(255,255,255,0.2),0_8px_18px_rgba(10,90,80,0.28)] hover:-translate-y-[2px] hover:shadow-[inset_0_1px_0_rgba(255,255,255,0.26),0_11px_24px_rgba(12,110,86,0.34)] active:scale-[0.985] ${disabledGray} ${darkField}`,
  dark: `text-white border-[rgba(35,100,190,0.28)] ${hoverNudge} ${disabledGray} focus-visible:ring-offset-bg-primary`,
  darkSecondary: "border-brand-darkText text-brand-dark bg-brand-darkText hover:bg-white hover:-translate-y-[1px] focus-visible:ring-offset-brand-dark",
  darkGhost: "border-white/25 text-brand-darkText bg-transparent hover:border-white/40 hover:bg-white/10 focus-visible:ring-offset-brand-dark"
};

export function Button({ children, to, href, external = false, download, type = "button", variant = "primary", size = "md", calm = false, arrow = true, onClick, className = "", disabled = false, loading = false }: ButtonProps) {
  const isDisabled = disabled || loading;
  const surface = variant === "primary" || variant === "dark" ? (calm ? calmSurface : primarySurface) : "";
  const classes = `group inline-flex items-center justify-center gap-2 rounded-[15px] border text-center font-semibold transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-blue/40 focus-visible:ring-offset-[3px] disabled:pointer-events-none ${sizes[size]} ${surface} ${variants[variant]} ${className}`;

  const content = (
    <>
      <span>{children}</span>
      {loading ? (
        <span className="h-4 w-4 animate-spin rounded-full border-2 border-white/40 border-t-white" aria-hidden="true" />
      ) : arrow ? (
        <ArrowRight className="rtl-flip h-4 w-4 transition-transform duration-200 group-hover:translate-x-[4px] group-focus-visible:translate-x-[4px]" aria-hidden="true" />
      ) : null}
    </>
  );

  if (href) {
    return (
      <a className={`group ${classes}`} href={href} download={download} target={external ? "_blank" : undefined} rel={external ? "noopener noreferrer" : undefined} aria-disabled={isDisabled}>
        {content}
      </a>
    );
  }

  if (to) {
    return (
      <Link className={`group ${classes}`} to={to} onClick={onClick} aria-disabled={isDisabled} aria-busy={loading || undefined}>
        {content}
      </Link>
    );
  }

  return (
    <button className={`group ${classes}`} type={type} onClick={onClick} disabled={isDisabled} aria-busy={loading || undefined}>
      {content}
    </button>
  );
}
